<template>
  <div>
    <!-- section-introduction -->
    <NuxtLayout name="default" :is-primary-navbar-sticky="isDesktopScreen">
      <div>
        <section class="bg-white pt-space-md pb-space-xs py-lg-space-lg">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-10">
                <h1 class="h1-large mb-space-sm">
                  {{ $page?.onlineDegreeProgramsPage?.title }}
                </h1>
                <div v-html="$page?.onlineDegreeProgramsPage?.description"></div>
              </div>
            </div>
          </div>
        </section>
        <!-- section-degree-search -->
        <section class="bg-white mb-space-md mb-md-space-lg mb-lg-space-xxl">
          <div class="container">
            <div class="row">
              <!-- filters column: visible only for desktop and above screens -->
              <div class="d-none d-lg-block col-lg-3">
                <div class="d-flex justify-content-between align-items-center pb-space-xs">
                  <h2 class="h2-small">{{ filterTitle }}</h2>
                  <button
                    v-show="hasAppliedFilters"
                    class="fs-small rds-link clear-btn"
                    data-cy="online-degree-programs-clear-all-filters-button"
                    @click="handleClearAll()"
                  >
                    Clear all
                  </button>
                </div>
                <ClientOnly>
                  <!-- highlight filter block -->
                  <div class="py-lg-space-xs border-bottom filter-categories">
                    <FilterCheckbox
                      v-model="selectedHighlights"
                      component-id="highlight-filters"
                      :title="PAGE_CONSTANTS.HIGHLIGHT_FILTER_TITLE"
                      :options="itemsForHighlightFilter"
                      show-tooltip
                      @checked="handleHighlightChecked"
                      @unchecked="handleHighlightUnchecked"
                    />
                  </div>
                  <!-- category filter block -->
                  <div class="py-lg-space-xs border-bottom filter-categories">
                    <FilterCheckbox
                      v-model="selectedDegreeTypes"
                      component-id="degree-type-filters"
                      data-cy="online-degree-programs-degree-type-filter"
                      :title="PAGE_CONSTANTS.DEGREE_TYPE_FILTER_TITLE"
                      :options="degreeTypeFilterOptions"
                      @checked="handleDegreeTypeChecked"
                      @unchecked="handleDegreeTypeUnchecked"
                    />
                  </div>
                  <!-- interest-areas/subject-areas filter block -->
                  <div class="py-lg-space-xs border-bottom filter-categories">
                    <FilterCheckboxNested
                      v-model="selectedInterestArea"
                      component-id="subject-areas-filters"
                      :title="PAGE_CONSTANTS.SUBJECT_AREAS_FILTER_TITLE"
                      :options="subjectAreaFilterOptions"
                      @checked="handleSubjectAreaChecked"
                      @unchecked="handleSubjectAreaUnchecked"
                      @check-all="handleInterestAreaChecked"
                      @uncheck-all="handleInterestAreaUnchecked"
                      @collapse:hidden="handleInterestAreaCollapseHidden"
                      @collapse:shown="handleInterestAreaCollapseShown"
                    />
                  </div>
                  <!-- colleges filter block -->
                  <div class="py-lg-space-xs filter-categories">
                    <FilterCollapseCheckbox
                      v-model="selectedColleges"
                      component-id="college-filters"
                      :title="PAGE_CONSTANTS.COLLEGE_FILTER_TITLE"
                      :options="collegesFilterOptions"
                      @checked="handleCollegeChecked"
                      @unchecked="handleCollegeUnchecked"
                      @collapse:hidden="handleCollegeCollapseHidden"
                      @collapse:shown="handleCollegeCollapseShown"
                    />
                  </div>
                </ClientOnly>
              </div>
              <!-- search column -->
              <div ref="mobileFilterButton" class="col-12 col-lg-9">
                <!-- search query row: visible only for desktop and above screens -->
                <div id="search-query" class="row">
                  <div class="col-12 mb-0">
                    <TypeAheadSearch
                      id="search-typeahead"
                      v-model="queries"
                      placeholder="Search program by title"
                      :options="filteredOptions"
                      @changed="handleSearchQueryChanged"
                    />
                  </div>
                </div>
                <!-- filter button & sidebar: visible only for mobile and tablet screens -->
                <div
                  class="row bg-white pt-space-xs pt-lg-0 mb-space-sm"
                  :class="{
                    'filter-div': !isDesktopScreen,
                    'sticky-top': !isDesktopScreen,
                    'pb-space-sm': !isDesktopScreen,
                    'border-bottom': !isDesktopScreen
                  }"
                >
                  <div class="d-block d-lg-none col-12">
                    <!-- add mobile loading -->
                    <div class="overlay-loading" :class="{ 'overlay-loading--show': pageLoading }">
                      <spinner-group
                        v-if="pageLoading"
                        class="overlay-loader"
                        spinner-style="border"
                      ></spinner-group>
                      <button
                        class="btn btn-dark-3 w-100 d-bold fw-bold text-white"
                        @click="handleCanvasAsideShow"
                      >
                        {{ filterTitle }}
                      </button>
                    </div>
                  </div>
                </div>
                <!-- program count row -->
                <div class="row">
                  <div class="col-12">
                    <div
                      class="d-flex justify-content-between align-items-center w-100 pb-space-sm"
                    >
                      <h2 class="fs-medium mb-0 fw-bold d-lg-none">
                        {{ resultsFiltered?.length ?? 0 }} programs available
                      </h2>

                      <p class="fs-medium mb-0 fw-bold d-none d-lg-block">
                        {{ resultsFiltered?.length ?? 0 }} programs available
                      </p>
                      <TabSwitcherApollo
                        v-if="storeLoading === false"
                        id="list-style-tab"
                        ref="tabSwitchComponent"
                        class="d-none d-lg-block"
                        :tabs="checkTabs"
                        button-size="sm"
                        @tab-click="handleTabClick"
                      >
                        <template #icon(1)>
                          <FontAwesomeIcon :icon="faGrid" class="cta-icon-class"></FontAwesomeIcon>
                        </template>
                        <template #icon(2)>
                          <FontAwesomeIcon :icon="faList" class="cta-icon-class"></FontAwesomeIcon>
                        </template>
                      </TabSwitcherApollo>
                    </div>
                    <div v-if="resultsFiltered?.length === 0" class="fs-small">
                      <p>
                        Sorry, no results were found based on your current search criteria. Please
                        try again.
                      </p>
                      <p>
                        <span class="fw-bold">Search suggestions:</span><br />
                        Check your spelling. <br />Clear your filters.<br />Try different words that
                        mean the same thing.<br />Try more general words.
                      </p>
                    </div>
                  </div>
                </div>
                <!-- search results row -->
                <template v-if="pageLoading">
                  <div class="status text-center">
                    <spinner-group spinner-style="border"></spinner-group>
                  </div>
                </template>
                <ClientOnly>
                  <div
                    :class="[
                      degreeDisplayStyle === 'cards'
                        ? 'row-cols-1 row-cols-md-2 row-cols-lg-3'
                        : 'row-cols-1',
                      'row'
                    ]"
                  >
                    <NuxtLink
                      v-for="result in resultsFiltered"
                      :key="result.item.degreeImage!"
                      :to="result.item.detailPage!"
                      :class="[
                        degreeDisplayStyle === 'cards'
                          ? 'mb-space-xs mb-lg-space-md'
                          : 'mb-space-xs',
                        'col d-flex'
                      ]"
                      data-cy="online-degree-programs-degree-search-card"
                      @click.prevent="handleDegreeCardLinkClick(result.item, degreeDisplayStyle)"
                    >
                      <CardDegreeSearch
                        v-if="degreeDisplayStyle === 'cards'"
                        class="flex-fill mx-0 text-dark-3"
                        border-variant="light-2"
                        :image-source="result.item.degreeImage!"
                        :image-alt="result.item.title!"
                        :label="result.item.category?.title!"
                        label-size="medium"
                        footer-label-size="medium"
                        :title="result.item.title!"
                        :show-description="false"
                        footer-cta-text="Learn more"
                        :footer-label="formattedFooterLabel(result.item.nextStartDate!)"
                        :footer-cta-link="result.item.detailPage!"
                      ></CardDegreeSearch>
                      <ProgramListItem
                        v-else
                        :title="result.item.title!"
                        :category="result.item.category?.title!"
                        :date="result.item.nextStartDate!"
                        :link="result.item.detailPage!"
                      ></ProgramListItem>
                    </NuxtLink>
                  </div>
                </ClientOnly>
              </div>
            </div>
          </div>
        </section>
        <!-- section-rfi -->
        <SectionRfiMultiStep />

        <OffCanvasAside
          v-model="isOffCanvasAsideOpen"
          direction="left"
          width="320px"
          teleport-to="body"
          bg-variant="white"
        >
          <header class="border-bottom p-space-xs">
            <div class="row">
              <div class="col-5">
                <button
                  :disabled="!hasAppliedFilters"
                  class="btn btn-light-2 fw-bold py-space-xxs d-block w-100"
                  @click="handleClearAll()"
                >
                  Clear all
                </button>
              </div>
              <div class="col-7">
                <button
                  class="btn btn-secondary fw-bold py-space-xxs d-block w-100"
                  @click="showMobileFilterResults"
                >
                  {{ sidebarApplyText }}
                </button>
              </div>
            </div>
          </header>
          <div class="sidebar-content px-space-xs overflow-y-auto">
            <!-- highlight filter block -->
            <div class="pb-space-xxs my-space-xs border-bottom filter-categories">
              <FilterCheckbox
                v-model="selectedHighlights"
                component-id="highlight-filters-mobile"
                :title="PAGE_CONSTANTS.HIGHLIGHT_FILTER_TITLE"
                :options="itemsForHighlightFilter"
                show-tooltip
                @checked="handleHighlightChecked"
                @unchecked="handleHighlightUnchecked"
              />
            </div>
            <!-- category filter block -->
            <div class="pb-space-xxs my-space-xs border-bottom filter-categories">
              <FilterCheckbox
                v-model="selectedDegreeTypes"
                component-id="degree-type-filters-mobile"
                :title="PAGE_CONSTANTS.DEGREE_TYPE_FILTER_TITLE"
                :options="degreeTypeFilterOptions"
                @checked="handleDegreeTypeChecked"
                @unchecked="handleDegreeTypeUnchecked"
              />
            </div>
            <!-- interest-areas/subject-areas filter block -->
            <div class="pb-space-xxs my-space-xs border-bottom filter-categories">
              <FilterCheckboxNested
                v-model="selectedInterestArea"
                component-id="subject-areas-filters-mobile"
                :title="PAGE_CONSTANTS.SUBJECT_AREAS_FILTER_TITLE"
                :options="subjectAreaFilterOptions"
                @checked="handleSubjectAreaChecked"
                @unchecked="handleSubjectAreaUnchecked"
                @check-all="handleInterestAreaChecked"
                @uncheck-all="handleInterestAreaUnchecked"
                @collapse:hidden="handleInterestAreaCollapseHidden"
                @collapse:shown="handleInterestAreaCollapseShown"
              />
            </div>
            <!-- colleges filter block -->
            <div class="pb-space-xxs my-space-xs filter-categories">
              <FilterCollapseCheckbox
                v-model="selectedColleges"
                component-id="college-filters-mobile"
                :title="PAGE_CONSTANTS.COLLEGE_FILTER_TITLE"
                :options="collegesFilterOptions"
                @checked="handleCollegeChecked"
                @unchecked="handleCollegeUnchecked"
                @collapse:hidden="handleCollegeCollapseHidden"
                @collapse:shown="handleCollegeCollapseShown"
              />
            </div>
          </div>
        </OffCanvasAside>
      </div>
    </NuxtLayout>
  </div>
</template>
<script setup lang="ts">
import type { FilterOption } from "@/components/degree-search/FilterCheckbox.vue";
import FilterCheckbox from "@/components/degree-search/FilterCheckbox.vue";
import { useUiStateStore } from "@/stores";
import { faList } from "@fortawesome/free-solid-svg-icons";
import { faGrid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import CardDegreeSearch from "@rds-vue-ui/card-degree-search/CardDegreeSearch.vue";
import { TabSwitcherApollo } from "@rds-vue-ui/tab-switcher-apollo";
import { debouncedRef, useSorted, useWindowSize } from "@vueuse/core";
import type { UseFuseOptions } from "@vueuse/integrations/useFuse";
import { useFuse } from "@vueuse/integrations/useFuse";
import type { FuseResult } from "fuse.js";
import { storeToRefs } from "pinia";
import OffCanvasAside from "~/components/OffCanvasAside.vue";
import SpinnerGroup from "~/components/SpinnerGroup.vue";
import TypeAheadSearch, { type TypeAheadSearchListOption } from "~/components/TypeAheadSearch.vue";
import type {
  FilterNestedModel,
  NestedCheckFilterEmit,
  NestedFilterCollapseEmit
} from "~/components/degree-search/FilterCheckboxNested.vue";
import FilterCheckboxNested from "~/components/degree-search/FilterCheckboxNested.vue";
import type { FilterCollapseOption } from "~/components/degree-search/FilterCollapseCheckbox.vue";
import FilterCollapseCheckbox from "~/components/degree-search/FilterCollapseCheckbox.vue";
import ProgramListItem from "~/components/degree-search/ProgramListItem.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import type { DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";
import type { FilterInterestAreaSubjects } from "~/src/services/DegreeSearchService";
import { extractUniqueSubjects, extractUniqueValues } from "~/src/services/DegreeSearchService";
import type { OnlineDegreeProgramsPageQuery } from "@/types/graphql-operations";

/*
 ********************************************
 * Page query
 ********************************************
 */
definePageMeta({
  layout: false,
  pageTransition: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});
const $page = await usePageQuery<OnlineDegreeProgramsPageQuery>(
  /* GraphQL */ `
    #graphql
    query OnlineDegreeProgramsPage(
      $onlineDegreeProgramsPageId: ID!
      $filter: filterInput
      $sort: sortInput
    ) {
      onlineDegreeProgramsPage(id: $onlineDegreeProgramsPageId) {
        detailPage
        title
        description
        metaTitle
        metaDescription
      }
      allDegreeSeekingProgram(filter: $filter, sort: $sort) {
        title
        alternateTitle
        detailPage
        degreeImage
        nextStartDate
        category {
          id
          slug
          title
        }
        college {
          id
          slug
          title
        }
        interestAreas {
          id
          slug
          title
          subjects {
            title
            slug
            id
          }
        }
        subjects {
          title
          slug
          id
        }
        highlights {
          title
          slug
          id
          description
        }
      }
    }
  `,
  {
    onlineDegreeProgramsPageId: "8cf49e8d-dd70-4279-b692-37269e92b00f",
    filter: {
      programFeature: {
        eq: "b4e8dd15-fed3-4594-a24b-ad8147dc9e26"
      }
    },
    sort: {
      by: "title",
      order: "ASC"
    }
  },
  {
    key: "all-degree-search"
  }
);

/*
 ********************************************
 * Page metadata
 ********************************************
 */
const jsonLdMarkup = useSchemaService().generateAllDegreeSearchSchema(
  $page?.onlineDegreeProgramsPage?.metaTitle as string,
  $page?.onlineDegreeProgramsPage?.metaDescription as string,
  $page?.onlineDegreeProgramsPage?.detailPage as string,
  ($page?.allDegreeSeekingProgram ?? []) as unknown as DegreeSeekingProgram[]
);
useHead({
  title: $page?.onlineDegreeProgramsPage?.metaTitle,
  meta: [
    {
      name: "description",
      content: $page?.onlineDegreeProgramsPage?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: $page?.onlineDegreeProgramsPage?.detailPage as string
    }
  ],
  script: [jsonLdMarkup]
});

/*
 ********************************************
 * Enums
 ********************************************
 */
enum PAGE_CONSTANTS {
  HIGHLIGHT_FILTER_TITLE = "Highlighted programs",
  DEGREE_TYPE_FILTER_TITLE = "Degree type",
  SUBJECT_AREAS_FILTER_TITLE = "Areas of interest",
  COLLEGE_FILTER_TITLE = "College",
  UPDATE_TEXT = "Update",
  CLOSE_TEXT = "Close",
  FILTERS_TEXT = "Filters",
  SECTION_TITLE = "All online degree programs"
}
/*
 ********************************************
 * Data
 ********************************************
 */
const selectedHighlights = ref<Set<string>>(new Set());
const selectedDegreeTypes = ref<Set<string>>(new Set());
const selectedColleges = ref<Set<string>>(new Set());
const isOffCanvasAsideOpen = ref<boolean>(false);
const pageLoading = ref<boolean>(true);
const uiStore = useUiStateStore();
// const programFilterStore = useProgramFilter();
const { degreeDisplayStyle, storeLoading } = storeToRefs(uiStore);
// const {
//   selectedColleges,
//   selectedDegreeTypes,
//   selectedHighlights,
//   selectedInterestArea,
//   selectedSubjectArea,
// } = storeToRefs(programFilterStore);
const tabSwitchComponent = ref<InstanceType<typeof TabSwitcherApollo>>();

const checkTabs = [
  {
    tabId: "cards",
    ariaLabel: "Card view"
  },
  {
    tabId: "list",
    ariaLabel: "List view"
  }
];

/*
 ********************************************
 * Computed
 ********************************************
 */

const filteredOptions = computed<TypeAheadSearchListOption[]>(() => {
  return (
    searchBarResults.value.map(({ item }) => {
      return {
        value: item?.title ?? "",
        text: item?.title ?? ""
      };
    }) || []
  );
});
const sidebarApplyText = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.UPDATE_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.CLOSE_TEXT;
});
const selectedSubjectArea = computed<Set<string>>(() => {
  return new Set<string>(
    Object.values(selectedInterestArea.value).flatMap((subject) => {
      return Array.from(subject);
    })
  );
});
const totalAppliedFilters = computed<number>(() => {
  return (
    selectedHighlights.value?.size +
    selectedDegreeTypes.value?.size +
    selectedColleges.value?.size +
    selectedSubjectArea.value?.size
  );
});
const hasAppliedFilters = computed<boolean>(() => {
  return totalAppliedFilters.value > 0;
});
const filterTitle = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.FILTERS_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.FILTERS_TEXT;
});

const degreeTypeFilterOptions = computed<FilterOption[]>(() => {
  return extractUniqueValues($page?.allDegreeSeekingProgram ?? [], "category");
});

const itemsForHighlightFilter = computed<FilterOption[]>(() => {
  const uniques = extractUniqueValues($page?.allDegreeSeekingProgram ?? [], "highlights");
  // programFilterStore.syncSetWithAllowedValues(selectedHighlights, uniques);
  return uniques;
});
const collegesFilterOptions = computed<FilterCollapseOption[]>(() => {
  const options = extractUniqueValues($page?.allDegreeSeekingProgram ?? [], "college");
  // programFilterStore.syncSetWithAllowedValues(selectedColleges, options);
  return options.sort((a, b) => a.text.localeCompare(b.text));
});

const subjectAreaFilterOptions = computed<FilterInterestAreaSubjects[]>(() => {
  return extractUniqueSubjects($page?.allDegreeSeekingProgram ?? [], "interestAreas");
});

const selectedInterestArea = ref<FilterNestedModel>(
  subjectAreaFilterOptions.value.reduce<FilterNestedModel>((acc, subjectArea) => {
    acc[subjectArea.value] = new Set<string>();
    return acc;
  }, {})
);
// programFilterStore.setselectedInterestArea(subjectAreaFilterOptions.value);

/*
 ********************************************
 * Methods
 ********************************************
 */
const handleClearAll = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "filters",
    "clear all",
    "filters"
  );
  // programFilterStore.clearAllFilter();
  selectedHighlights.value.clear();
  selectedDegreeTypes.value.clear();
  selectedColleges.value.clear();
  for (const [key] of Object.entries(selectedInterestArea.value)) {
    selectedInterestArea.value[key].clear();
  }
};
const handleCanvasAsideShow = () => {
  isOffCanvasAsideOpen.value = true;
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    "filters",
    "filters"
  );
};
const formattedFooterLabel = (nextStartDate: string) => {
  return `Next start date: ${nextStartDate}`;
};

const mobileFilterButton = ref<HTMLElement | null>(null);
const showMobileFilterResults = () => {
  // scroll to searchbar
  mobileFilterButton.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest"
  });
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    "filters",
    sidebarApplyText.value.toLowerCase()
  );
  isOffCanvasAsideOpen.value = false;
};

const handleTabClick = (e: Event, idx: number) => {
  e.preventDefault();
  let view: "card view" | "list view";
  if (idx === 0) {
    uiStore.setDisplayStyle("cards");
    view = "card view";
  } else {
    uiStore.setDisplayStyle("list");
    view = "list view";
  }
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.onlineDegreeProgramsPage?.title?.toLowerCase() as string,
    view
  );
};
const initializeTabStatus = () => {
  if (degreeDisplayStyle.value === "cards") {
    tabSwitchComponent.value?.setActiveTab(0);
  } else {
    tabSwitchComponent.value?.setActiveTab(1);
  }
};

/*
 ********************************************
 * Analytics
 ********************************************
 */
const handleDegreeCardLinkClick = async (item: DegreeSeekingProgram, display: "cards" | "list") => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    item.title?.toLowerCase() || "N/A",
    `degree ${display}`
  );
};
const handleHighlightChecked = (option: FilterOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "highlighted programs",
    option.text.toLowerCase(),
    "filters"
  );
};
const handleHighlightUnchecked = (option: FilterOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "highlighted programs",
    option.text.toLowerCase(),
    "filters"
  );
};

const handleDegreeTypeChecked = (option: FilterOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "degree type",
    option?.text.toLowerCase(),
    "filters"
  );
};
const handleDegreeTypeUnchecked = (option: FilterOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "degree type",
    option?.text.toLowerCase(),
    "filters"
  );
};

const handleInterestAreaChecked = (option: NestedCheckFilterEmit) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "areas of interest",
    option.text.toLowerCase()
  );
};
const handleInterestAreaUnchecked = (option: NestedCheckFilterEmit) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "areas of interest",
    option.text.toLowerCase()
  );
};
const handleSubjectAreaChecked = (option: NestedCheckFilterEmit) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "areas of interest",
    option.text.toLowerCase(),
    "filters"
  );
};
const handleSubjectAreaUnchecked = (option: NestedCheckFilterEmit) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "areas of interest",
    option.text.toLowerCase(),
    "filters"
  );
};

const resolveInterestAreaTitle = (slug: string): string => {
  const matchingObj = subjectAreaFilterOptions.value.find(
    (item) => item.value.toLowerCase() === slug.toLowerCase()
  );
  return matchingObj?.text.toLowerCase() || " ";
};

const handleInterestAreaCollapseHidden = (collapse: NestedFilterCollapseEmit) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    collapse.event,
    "click",
    "main content",
    "areas of interest",
    resolveInterestAreaTitle(collapse.value),
    "filters"
  );
};

const handleInterestAreaCollapseShown = (collapse: NestedFilterCollapseEmit) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    collapse.event,
    "click",
    "main content",
    "areas of interest",
    resolveInterestAreaTitle(collapse.value),
    "filters"
  );
};

const handleCollegeChecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "checkbox",
    "main content",
    "college",
    option.text.toLowerCase(),
    "filters"
  );
};
const handleCollegeUnchecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unclick",
    "checkbox",
    "main content",
    "college",
    option.text.toLowerCase(),
    "filters"
  );
};

const handleCollegeCollapseHidden = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "close",
    "click",
    "main content",
    "filters",
    "college"
  );
};
const handleCollegeCollapseShown = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "open",
    "click",
    "main content",
    "filters",
    "college"
  );
};
interface SearchOption {
  value: string;
  text: string;
}
const handleSearchQueryChanged = (searchOption: SearchOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "search",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    searchOption.text.toLocaleLowerCase(),
    "search bar"
  );
};
/*
 ********************************************
 * Reactive
 ********************************************
 */
const { width } = useWindowSize();
const isDesktopScreen = computed(() => width.value >= 992);

/*
 ********************************************
 * Fuse search
 ********************************************
 */

const fuseOptions: UseFuseOptions<DegreeSeekingProgram> = {
  fuseOptions: {
    // set minMatchCharLength to 1 otherwise search bar will not filter programs with long title
    isCaseSensitive: false,
    includeScore: true,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.6,
    distance: 200,
    keys: ["title", "category.slug", "subjects.slug", "college.slug", "highlights.slug"]
  },
  matchAllWhenSearchEmpty: true
};
const queries = ref<string | null>(null);
const searchOptions = ref<DegreeSeekingProgram[]>(
  ($page?.allDegreeSeekingProgram ?? []) as unknown as DegreeSeekingProgram[]
);
// matchAllWhenSearchEmpty: true, to show all programs when search bar is empty
const { fuse, results } = useFuse("", searchOptions, fuseOptions);
const resultsFiltered = ref<FuseResult<DegreeSeekingProgram>[]>(results.value ?? []);
const searchBarResults = ref<FuseResult<DegreeSeekingProgram>[]>(results.value ?? []);

const debouncedSearchQuery = debouncedRef(queries, 350);

const preparedSchema = computed(() => {
  const schema = [];
  if (selectedHighlights.value?.size > 0) {
    schema.push({
      $or: Array.from(selectedHighlights.value).map((highlight) => {
        return { "highlights.slug": `=${highlight}` };
      })
    });
  }
  if (selectedDegreeTypes.value?.size > 0) {
    schema.push({
      $or: Array.from(selectedDegreeTypes.value).map((degreeType) => {
        return { "category.slug": `=${degreeType}` };
      })
    });
  }
  if (selectedSubjectArea.value?.size > 0) {
    schema.push({
      $or: Array.from(selectedSubjectArea.value).map((subject) => {
        return { "subjects.slug": `=${subject}` };
      })
    });
  }
  if (selectedColleges.value?.size > 0) {
    schema.push({
      $or: Array.from(selectedColleges.value).map((college) => {
        return { "college.slug": `=${college}` };
      })
    });
  }
  return schema;
});

const searchQuerySchema = computed(() => {
  if (debouncedSearchQuery.value && debouncedSearchQuery.value.length > 0) {
    return { title: debouncedSearchQuery.value };
  }
  return null;
});

// watch(
//   [
//     selectedHighlights,
//     selectedDegreeTypes,
//     selectedColleges,
//     selectedInterestArea,
//   ],
//   () => {
//     programFilterStore.saveStates();
//   },
//   { deep: true }
// );

watchEffect(() => {
  if (preparedSchema.value.length > 0) {
    const results = fuse.value.search({
      $and: preparedSchema.value
    });
    resultsFiltered.value = useSorted(results, (a, b) =>
      a.item.title!.localeCompare(b.item.title!)
    ).value;
    searchBarResults.value = results;
  } else {
    resultsFiltered.value = results.value;
    searchBarResults.value = results.value;
  }
  if (debouncedSearchQuery.value !== null && debouncedSearchQuery.value.length > 0) {
    const schema = [];
    if (preparedSchema.value.length > 0) {
      preparedSchema.value.forEach((item) => {
        schema.push(item);
      });
    }
    schema.push(searchQuerySchema.value!);
    const results = fuse.value.search({
      $and: schema
    });
    resultsFiltered.value = results;
    searchBarResults.value = results;
  }
  if (isDesktopScreen.value === false) {
    uiStore.setDisplayStyleRef("list");
    initializeTabStatus();
  } else {
    uiStore.syncState();
    initializeTabStatus();
  }
});

onMounted(() => {
  const { interest, degree, college } = useRoute().query;
  if (degree && degree !== "all-degrees") {
    selectedDegreeTypes.value.add(degree as string);
  }
  if (interest && interest !== "all-interest-areas") {
    subjectAreaFilterOptions.value.forEach((interestArea) => {
      if (interestArea.value === interest) {
        interestArea.subjects.forEach((subject) => {
          selectedInterestArea.value[interestArea.value].add(subject.value);
        });
      }
    });
  }
  if (college && college !== "all-colleges") {
    selectedColleges.value.add(college as string);
  }
  pageLoading.value = false;
  initializeTabStatus();
});
</script>

<style lang="scss" scoped>
.filter-div {
  z-index: 1;
}
.status {
  min-height: 700px;
}
.clear-btn {
  color: var(--primary, #8c1d40);
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.overlay-loading {
  &--show {
    position: relative;
    display: block;
    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--rds-dark-3);
      opacity: 0.8;
      z-index: 3;
    }
    .overlay-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 4;
    }
  }
}
:deep(.tab-list) {
  padding: 4px !important;
  gap: 4px !important;
}
:deep(.tab-list .highlight) {
  top: 4px !important;
  bottom: 4px !important;
}
</style>
<style style="scss">
.filter-categories {
  .form-check-input:checked {
    background-color: var(--rds-secondary);
    border-color: var(--rds-secondary);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%23191919' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3e%3c/svg%3e");
    background-size: 66%;
  }
}
</style>
